/**
 * SystemModulesPage — F12.M Etap C.1.
 *
 * Lista 5 modułów systemowych tenanta: Kontrahenci / Pracownicy / Firmy /
 * Użytkownicy / Kontakty kontrahentów. Pre-seedowane przy provisioning
 * (`tenant-provisioner.ts` + `seedSystemModules`). Slug to PK string.
 *
 * Różnice vs `ModulesPage` (moduły niestandardowe):
 *   - Brak tworzenia (5 modułów seedowanych, `is_system_locked=true`)
 *   - Brak usuwania (locked)
 *   - Brak toggle „pokaż usunięte" (soft-delete N/A)
 *   - Brak edycji icon/name/color w karcie — edycja w hub Settings
 *   - Klik w kafelek → `/system-modules/:slug/settings` (hub)
 *
 * Routing: `/system-modules` (tenant scope, ModuleGuard 'crm_core' +
 *   permission `crm_core.system_modules.view`).
 */

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { Card } from '@/components/Card';
import { renderIconByName } from '@/components/modules/IconPicker';
import { Spinner } from '@/components/Spinner';
import { api } from '@/lib/api';

interface SystemModuleListItem {
  slug: string;
  name: string;
  entityTableName: string;
  extTableName: string;
  icon: string;
  color: string;
  isSystemLocked: boolean;
  createdAt: string;
  updatedAt: string;
}

export function SystemModulesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const modulesQuery = useQuery({
    queryKey: ['system-modules'],
    queryFn: async () => {
      const res = await api.get<{ modules: SystemModuleListItem[] }>('/system-modules');
      return res.data.modules;
    },
  });

  return (
    <main className="min-h-full bg-bg-secondary p-6" data-testid="system-modules-page">
      <div className="space-y-5">
        <header>
          <h1 className="text-2xl font-bold text-text-primary">
            {t('tenant.systemModules.title')}
          </h1>
          <p className="mt-1 text-sm text-text-tertiary">{t('tenant.systemModules.subtitle')}</p>
        </header>

        {modulesQuery.isLoading && (
          <div className="flex justify-center py-12" data-testid="system-modules-loading">
            <Spinner />
          </div>
        )}

        {modulesQuery.isError && (
          <Card padding="md">
            <p className="text-error-500" data-testid="system-modules-load-error">
              {isAxiosError(modulesQuery.error) && modulesQuery.error.response?.status === 403
                ? t('tenant.systemModules.forbidden')
                : t('tenant.systemModules.loadError')}
            </p>
          </Card>
        )}

        {modulesQuery.data && modulesQuery.data.length > 0 && (
          <div
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="system-modules-grid"
          >
            {modulesQuery.data.map((mod) => (
              <SystemModuleCard
                key={mod.slug}
                mod={mod}
                onClick={() => navigate(`/system-modules/${mod.slug}/settings`)}
              />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

// ─── Kafelek modułu systemowego ─────────────────────────────────────────────

interface SystemModuleCardProps {
  mod: SystemModuleListItem;
  onClick: () => void;
}

function SystemModuleCard({ mod, onClick }: SystemModuleCardProps) {
  const { t } = useTranslation();

  return (
    <Card
      padding="md"
      className="cursor-pointer transition hover:border-accent-500 hover:shadow-md"
      onClick={onClick}
      data-testid={`system-module-card-${mod.slug}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${mod.color}20`, color: mod.color }}
          aria-hidden="true"
        >
          {renderIconByName(mod.icon, 'h-5 w-5')}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-text-primary">{mod.name}</h3>
            <span
              className="rounded-full bg-bg-tertiary px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-muted"
              title={t('tenant.systemModules.lockedTooltip')}
            >
              {t('tenant.systemModules.coreBadge')}
            </span>
          </div>
          <p className="mt-1 text-xs text-text-tertiary">
            {t('tenant.systemModules.entityLabel', { entity: mod.entityTableName })}
          </p>
          <p className="mt-2 text-xs text-text-muted">/{mod.slug}</p>
        </div>
      </div>
    </Card>
  );
}
